/**
 * M3 SHAPING — the pass that turns a library clip into ours.
 *
 * B3a proved Mixamo library clips bind natively to these rigs. That bought
 * the ability to animate; it did not buy the look. Dropped in unmodified,
 * realistic mocap reproduces exactly the generic-stock read this program
 * exists to leave behind, so **a clip that plays but was not shaped is a
 * FAIL, not partial credit.**
 *
 * Everything here is RENDER LAYER. Clips change what is drawn, never what is
 * simulated: the state-hash and eventHash trails are identical with animation
 * on or off (M10). Curves come from `ease.ts` and nowhere else (M8).
 *
 * The functions below MUTATE a cloned clip. Callers pass a clone; the loader
 * keeps the untouched library clip so a shaping mistake is never destructive.
 */
import {
  AnimationClip, KeyframeTrack, QuaternionKeyframeTrack, VectorKeyframeTrack,
  Quaternion, Euler,
} from 'three';
import { bob, inCubic, inQuint, outCubic, settle, threePhase } from './ease.js';

/** Track name -> bone, for the "does this track drive bone X" tests below. */
const boneOf = (trackName: string): string => trackName.split('.')[0] ?? '';
const isRot = (t: KeyframeTrack): t is QuaternionKeyframeTrack =>
  t instanceof QuaternionKeyframeTrack;
const isVec = (t: KeyframeTrack): t is VectorKeyframeTrack =>
  t instanceof VectorKeyframeTrack;

/**
 * Resample every track onto a new time base through `remap`.
 *
 * `remap` takes normalised time and returns normalised time, so it can slow
 * one phase and whip another. Resampling rather than merely scaling the clip
 * is what makes the phases readable: scaling changes how long the whole
 * action takes, remapping changes where its weight sits.
 */
function retime(clip: AnimationClip, remap: (t: number) => number, steps = 48): void {
  const dur = clip.duration;
  if (dur <= 0) return;
  for (const track of clip.tracks) {
    const stride = track.getValueSize();
    const times = new Float32Array(steps);
    const values = new Float32Array(steps * stride);
    const q0 = new Quaternion(); const q1 = new Quaternion(); const qo = new Quaternion();
    for (let i = 0; i < steps; i++) {
      const u = i / (steps - 1);
      times[i] = u * dur;
      // Sample the ORIGINAL curve at the remapped time.
      const src = remap(u) * dur;
      // Find the bracketing original keys.
      let k = 0;
      while (k < track.times.length - 1 && track.times[k + 1]! < src) k++;
      const t0 = track.times[k]!;
      const t1 = track.times[Math.min(k + 1, track.times.length - 1)]!;
      const f = t1 > t0 ? (src - t0) / (t1 - t0) : 0;
      const o0 = k * stride;
      const o1 = Math.min(k + 1, track.times.length - 1) * stride;
      if (stride === 4) {
        // Quaternions must SLERP. Component-wise lerp on a rotation shortens
        // the arc and reads as a joint collapsing through itself.
        q0.fromArray(track.values as unknown as number[], o0);
        q1.fromArray(track.values as unknown as number[], o1);
        qo.copy(q0).slerp(q1, f);
        qo.toArray(values as unknown as number[], i * stride);
      } else {
        for (let c = 0; c < stride; c++) {
          const a = track.values[o0 + c]!; const b = track.values[o1 + c]!;
          values[i * stride + c] = a + (b - a) * f;
        }
      }
    }
    (track.times as unknown as Float32Array) = times;
    (track.values as unknown as Float32Array) = values;
  }
}

/** Add a rotation offset onto every key of the tracks driving `bones`. */
function addRotation(
  clip: AnimationClip, match: RegExp, euler: Euler,
  weight: (u: number) => number = () => 1,
): void {
  const off = new Quaternion().setFromEuler(euler);
  const cur = new Quaternion(); const mix = new Quaternion();
  const id = new Quaternion();
  for (const track of clip.tracks) {
    if (!isRot(track) || !match.test(boneOf(track.name))) continue;
    const n = track.times.length;
    for (let i = 0; i < n; i++) {
      const u = n > 1 ? i / (n - 1) : 0;
      cur.fromArray(track.values as unknown as number[], i * 4);
      // Scale the offset by the weight curve, then pre-multiply so the offset
      // is applied in the PARENT's space — a lean is a rotation of the body,
      // not of the bone about its own axis.
      mix.copy(id).slerp(off, weight(u));
      cur.premultiply(mix);
      cur.toArray(track.values as unknown as number[], i * 4);
    }
  }
}

/** Add a value to one component of a vector track. */
function addToComponent(
  clip: AnimationClip, match: RegExp, comp: number, f: (u: number) => number,
): void {
  for (const track of clip.tracks) {
    if (!isVec(track) || !match.test(boneOf(track.name))) continue;
    const stride = track.getValueSize();
    const n = track.times.length;
    for (let i = 0; i < n; i++) {
      const u = n > 1 ? i / (n - 1) : 0;
      track.values[i * stride + comp] = (track.values[i * stride + comp] ?? 0) + f(u);
    }
  }
}

/**
 * Shift matching tracks in TIME, wrapping within the clip.
 *
 * Resamples the curve at (t + frac*duration) rather than rotating the key
 * ARRAY by an integer index. The index version could not express 6% of a
 * cycle on a clip with few keys — `round(n * 0.06)` collapsed to zero or one
 * key — which is why the measured antiphase came back unchanged at 0.7857 ->
 * 0.7811. A phase offset is a continuous quantity and has to be sampled like
 * one.
 */
function phaseShiftTime(clip: AnimationClip, match: RegExp, frac: number): void {
  const dur = clip.duration;
  if (dur <= 0 || frac === 0) return;
  for (const track of clip.tracks) {
    if (!match.test(boneOf(track.name))) continue;
    const stride = track.getValueSize();
    const n = track.times.length;
    const src = track.values.slice();
    const q0 = new Quaternion(); const q1 = new Quaternion(); const qo = new Quaternion();
    for (let i = 0; i < n; i++) {
      // Where this key should now read FROM, wrapped into the cycle.
      let t = track.times[i]! + frac * dur;
      while (t > dur) t -= dur;
      while (t < 0) t += dur;
      let k = 0;
      while (k < n - 1 && track.times[k + 1]! < t) k++;
      const k1 = Math.min(k + 1, n - 1);
      const t0 = track.times[k]!; const t1 = track.times[k1]!;
      const f = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
      if (stride === 4) {
        q0.fromArray(src as unknown as number[], k * 4);
        q1.fromArray(src as unknown as number[], k1 * 4);
        qo.copy(q0).slerp(q1, f);
        qo.toArray(track.values as unknown as number[], i * 4);
      } else {
        for (let c = 0; c < stride; c++) {
          const a = src[k * stride + c]!; const b = src[k1 * stride + c]!;
          track.values[i * stride + c] = a + (b - a) * f;
        }
      }
    }
  }
}

/**
 * Mean pitch of a bone across the clip, in radians.
 *
 * Needed because the source run is ALREADY pitched forward: an additive lean
 * stacks on it and reads as a hunch, while a counter-rotation that fixes the
 * hunch cancels the lean entirely. Both were measured on the strip. Knowing
 * the source's own mean lets the lean be SET rather than nudged.
 */
function meanPitch(clip: AnimationClip, match: RegExp): number {
  const e = new Euler(); const q = new Quaternion();
  let sum = 0, count = 0;
  for (const track of clip.tracks) {
    if (!isRot(track) || !match.test(boneOf(track.name))) continue;
    const n = track.times.length;
    for (let i = 0; i < n; i++) {
      q.fromArray(track.values as unknown as number[], i * 4);
      e.setFromQuaternion(q, 'XYZ');
      sum += e.x; count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

const HIPS = /Hips$/;
const SPINE = /Spine\d?$/;
const LEFT_LEG = /Left(UpLeg|Leg|Foot|ToeBase)$/;

/**
 * M3 RUN: vertical bob, forward lean, asymmetric footfall.
 *
 * `speed01` is how fast the body is moving relative to its top speed; the
 * lean is proportional to it, because a body sprinting and a body jogging
 * lean differently and a fixed lean reads as a costume rather than as effort.
 */
export function shapeRun(clip: AnimationClip, speed01 = 1): AnimationClip {
  const c = clip.clone();
  // Two bobs per stride cycle: the body rises on each foot, not once a loop.
  addToComponent(c, HIPS, 1, (u) => bob(u * 2) * 0.035 * (0.5 + speed01 * 0.5));
  // LEAN SET ABSOLUTELY, not added. The source run carries its own pitch, so
  // adding to it hunches the body and countering it cancels the lean — both
  // measured. Targeting a total lean and correcting by the difference gives
  // the same result on any source clip, which is what makes this reusable
  // for the fodder clips rather than tuned to one animation.
  const target = 0.16 * speed01;
  addRotation(c, HIPS, new Euler(target - meanPitch(clip, HIPS), 0, 0));
  // 6% of a cycle, on RESAMPLED time so the offset survives a sparse clip.
  // Kept small deliberately: asymmetry that reads as a limp is worse than a
  // metronome, and the limp check lives in the 10s clip, not in a still.
  phaseShiftTime(c, LEFT_LEG, 0.06);
  c.name = `${clip.name}:m3`;
  return c;
}

/**
 * M3 FIRE: anticipation -> impact -> recovery, and the recoil SETTLES.
 *
 * A looped shoot clip has no beats — it is one smear repeated, which is why
 * firing currently reads as vibration rather than as shots. The remap gives
 * the wind-up, the whip and the return distinct durations; the settle curve
 * on the spine lets the recoil overshoot and come back instead of snapping.
 */
export function shapeFire(clip: AnimationClip): AnimationClip {
  const c = clip.clone();
  retime(c, (t) => threePhase(t, 0.35, 0.5));
  // Recoil: the chest kicks back at impact, then settles. Weighted so nothing
  // happens during the wind-up and the whole motion lives after it.
  // ANTICIPATION: the chest gathers FORWARD before the shot. Without it the
  // clip has two beats, not three, and the strip showed impact and recovery
  // reading as one pose.
  addRotation(c, SPINE, new Euler(0.13, 0, 0), (u) =>
    (u < 0.35 ? outCubic(u / 0.35) : 0));
  // IMPACT + RECOVERY: kick back hard enough to change the OUTLINE, then
  // settle. 0.16rad was invisible at 400px; the bar is the silhouette.
  addRotation(c, SPINE, new Euler(-0.34, 0, 0), (u) =>
    (u < 0.35 ? 0 : (1 - settle((u - 0.35) / 0.65)) * 1.0));
  // The gun arm rides the recoil, a beat behind the chest.
  addRotation(c, /RightArm$|RightForeArm$/, new Euler(-0.22, 0, 0), (u) =>
    (u < 0.4 ? 0 : (1 - settle((u - 0.4) / 0.6, 5, 2.0))));
  // The hips absorb a fraction of it, a beat later — weight travelling down.
  addRotation(c, HIPS, new Euler(-0.05, 0, 0), (u) =>
    (u < 0.45 ? 0 : outCubic((u - 0.45) / 0.55)));
  c.name = `${clip.name}:m3`;
  return c;
}

/**
 * M5 HIT: the flinch must be READABLE within 100ms of the event.
 *
 * Mocap impacts ease in over a third of a second, which at this game's rate
 * of fire means the body is still winding up when the next round lands — the
 * reason the current build reads as bodies absorbing nothing. The remap front
 * -loads the pose: the whip is over almost immediately and the rest of the
 * clip is recovery.
 *
 * `dur` is the clip's own length; `ms` the budget the flinch must land in.
 * The displacement is carried into the SPINE and HIPS, not the arms alone —
 * a body whose arms flap while its torso holds still reads as a puppet.
 */
export function shapeHit(clip: AnimationClip, ms = 100): AnimationClip {
  const c = clip.clone();
  const onset = Math.min(0.5, Math.max(0.05, ms / 1000 / Math.max(0.001, c.duration)));
  // Whip through the source's first beat inside the onset budget, then play
  // the remainder out slowly as recovery.
  retime(c, (t) => (t < onset
    ? inQuint(t / onset) * 0.45
    : 0.45 + outCubic((t - onset) / (1 - onset)) * 0.55));
  // Torso takes the blow: a sharp yield that recovers, weighted to the onset.
  addRotation(c, SPINE, new Euler(-0.30, 0, 0.10), (u) =>
    (u < onset ? outCubic(u / onset) : 1 - outCubic((u - onset) / (1 - onset))));
  addRotation(c, HIPS, new Euler(-0.12, 0, 0), (u) =>
    (u < onset ? outCubic(u / onset) : 1 - outCubic((u - onset) / (1 - onset))));
  c.name = `${clip.name}:m3`;
  return c;
}

/**
 * M3 DEATH: a fall that accelerates, lands, and SETTLES.
 *
 * The bar here is Iron Rule 8 — the body must come to rest in a physically
 * possible pose. No fade and no despawn are playback decisions (the action
 * clamps on its last frame rather than looping or being removed); what the
 * CLIP owns is the arc: a fall eased like gravity rather than like a lerp,
 * and a settle at the end so the body arrives rather than stopping dead.
 *
 * The tail is deliberately slowed rather than trimmed. A death cut short is
 * exactly the mid-air freeze the rule forbids.
 */
export function shapeDeath(clip: AnimationClip): AnimationClip {
  const c = clip.clone();
  // Gravity, not a lerp: slow release, fast fall, then the ground arrests it.
  retime(c, (t) => (t < 0.75
    ? inCubic(t / 0.75) * 0.82
    : 0.82 + outCubic((t - 0.75) / 0.25) * 0.18));
  // The last fifth is the settle — weight redistributing after contact.
  addRotation(c, SPINE, new Euler(0.06, 0, 0), (u) =>
    (u < 0.8 ? 0 : 1 - settle((u - 0.8) / 0.2, 8, 1.6)));
  c.name = `${clip.name}:m3`;
  return c;
}

/**
 * Remove horizontal root translation, leaving the cycle IN PLACE.
 *
 * Needed because the deck does not move. `scene3d` sets `const scroll = 0`:
 * enemies and gates advance on their own in the sim while the bridge stands
 * still and the squad holds its ground. On ground that never translates, ANY
 * foot travel is skating by definition — and a clip carrying root motion
 * against a static deck reads as the lurch-and-snap the founder saw, because
 * the body creeps forward across the cycle and is reset at the loop point.
 *
 * Vertical translation is KEPT. The hip bob is what makes a stride read as
 * weight, and it is not motion across the ground.
 */
export function stripRootMotion(clip: AnimationClip): AnimationClip {
  const c = clip.clone();
  for (const track of c.tracks) {
    if (!isVec(track) || !/Hips$/.test(boneOf(track.name))) continue;
    const stride = track.getValueSize();
    if (stride < 3) continue;
    // Pin X and Z to their first value; leave Y alone.
    const x0 = track.values[0]!;
    const z0 = track.values[2]!;
    for (let i = 0; i < track.times.length; i++) {
      track.values[i * stride] = x0;
      track.values[i * stride + 2] = z0;
    }
  }
  // NO rename here. This always feeds straight into `shapeRun`, which is
  // the one place `:m3` gets appended for every shaped clip (shoot, die,
  // hit, run alike) — both `createCrowd`'s `ranges` map and the skinned
  // soldiers' `actions[clip.name]` key on the LITERAL final name with no
  // stripping, so a clip named `run:inplace:m3` registers under a name
  // nothing ever looks up (`squadClips.has('run')` is false), the FSM
  // falls back to a differently-resolved 'run', and the in-place fix
  // never actually reaches the screen. Keeping `clip.name` unchanged here
  // is what makes the final name come out as `run:m3`, matching every
  // other shaped clip.
  return c;
}
